import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import FormEditor from "../components/Form/Editor/FormEditor";
import Form from "../models/serviceModels/Form";
import EditableFormField from '../models/pageModels/EditableFormField';
import { createPublicFormInstance, getPublicForm } from "../services/formsService";
import Loader from "../components/UI/Loader";
import Button from "../components/UI/Button";
import ErrorMessages from "../components/UI/ErrorMessages";
import { mapFormFieldToEditableFormField, normalizeFormInstanceFieldValue } from "../mappers/formFieldMapper";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import { UPLOADED } from "../constants/fileUploadStatus";
import toast from "react-hot-toast";
import { normalizeDemographicsFormField } from "../utils/demographicsSchema";
import { configuredRequiredFieldsOrgSettingsEnabled, optionalDateOfBirthEnabled } from "../utils/features";

export default function PublicForm() {
    const { id } = useParams();
    const [apiError, setApiError] = useState<any>();
    const [errors, setErrors] = useState<string[]>([]);
    const [fields, setFields] = useState<EditableFormField[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState<Form>();
    const [showThankyouMessage, setShowThankyouMessage] = useState(false);
    const [formNotFound, setFormNotFound] = useState(false);

    useEffect(() => {
        document.title = "Public Form"
    }, []);

    useEffect(() => {
        if (!id) {
            return;
        }

        setLoading(true);
        setApiError(undefined);
        setErrors([]);

        getPublicForm(id).then(f => {
            const normalizedForm = {
                ...f,
                fields: f.fields.map(field => normalizePublicFormField(field))
            };

            setForm(normalizedForm);
            setFields([
                ...normalizedForm.fields.map(fe => mapFormFieldToEditableFormField(fe))
            ]);
        }).catch(e => {
            if (e?.response?.status === 404) {
                setFormNotFound(true);
            } else {
                console.error(e);
                setApiError(e);
            }
        }).finally(() => {
            setLoading(false);
        });
    }, [id]);

    const handleOnFieldsChange = (fields: EditableFormField[]) => {
        setFields([
            ...fields
        ]);
    };

    const handleSubmitForm = (e: React.FormEvent) => {
        e.preventDefault();
        setErrors([]);
        setApiError(undefined);

        if (!id) {
            return;
        }

        setSubmitting(true);

        createPublicFormInstance({
            publicFormId: id,
            fields: fields.map(f => {
                return {
                    fieldId: f.id,
                    value: normalizeFormInstanceFieldValue(f, f.value),
                    files: f.files?.map(f => f.fileId!) || []
                }
            })
        }).then(() => {
            toast.success('Successfully submitted!');
            setShowThankyouMessage(true);
            handleFormReset();
        }).catch(e => {
            toast.error('Failed to submit');
            setApiError(e);
            console.error(e);
        }).finally(() => {
            setSubmitting(false);
        })
    };

    const handleFormReset = () => {
        setErrors([]);
        setApiError(undefined);
        setFields([
            ...fields.map(f => {
                return {
                    ...f,
                    value: normalizeFormInstanceFieldValue(f),
                    files: []
                }
            })
        ])
    };

    const handleOnPrint = () => {
        window.print();
    }

    const getMainContent = () => {
        if (showThankyouMessage) {
            const useDefault = form?.useDefaultThankYouMessage !== false;
            const hasCustomMessage = !useDefault && form?.thankYouMessage && form.thankYouMessage.trim().length > 0;
            const showNoMessage = !useDefault && !hasCustomMessage;

            // If no message option is selected, just show the "Fill Out Again" button
            if (showNoMessage) {
                return <>
                    <div className="row margin-top-3 justify-content-center">
                        <div className="column-auto">
                            <Button id="submit-again-button"
                                text="Fill Out Again"
                                className="secondary"
                                onClick={() => setShowThankyouMessage(false)}
                            />
                        </div>
                    </div>
                </>;
            }

            return <>
                <div className="row margin-top-3 justify-content-center">
                    <div className="column-auto">
                        {hasCustomMessage ? (
                            <div style={{ whiteSpace: 'pre-wrap' }}>{form.thankYouMessage}</div>
                        ) : (
                            <>
                                <div className="text-bold">Thank you!</div>
                                <div>Your submission is on its way for review.</div>
                            </>
                        )}
                        <div className="row margin-top-3 justify-content-center">
                            <div className="column-auto">
                                <Button id="submit-again-button"
                                    text="Fill Out Again"
                                    className="secondary"
                                    onClick={() => setShowThankyouMessage(false)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </>;
        }

        return (<>
            {form && fields && fields.length > 0 && <div className="row margin-top-3">
                <div className="column">
                    <form onSubmit={handleSubmitForm}>
                        <ErrorMessages messages={errors} />
                        <ApiErrorMessages error={apiError} />
                        <div className="row justify-content-end hide-on-print">
                            <div className="column-auto">
                                <Button id="print-button"
                                    className="secondary"
                                    text="Print"
                                    onClick={handleOnPrint} />
                            </div>
                        </div>
                        <div className="column">
                            <FormEditor
                                disabled={false}
                                fields={fields}
                                onChange={handleOnFieldsChange}
                                publicFormId={id} />
                        </div>
                        <div className="row margin-top-3 justify-content-between hide-on-print">
                            <div className="column-auto">
                                <Button id="reset-button"
                                    className="secondary"
                                    text="Reset"
                                    disabled={submitting}
                                    onClick={handleFormReset} />
                            </div>
                            <div className="column-auto">
                                <Button id="submit-button"
                                    text="Submit"
                                    disabled={submitting || fields.some(f => f.files && f.files.some(ff => ff.status !== UPLOADED))}
                                    loading={submitting}
                                    type="submit" />
                            </div>
                        </div>
                    </form>
                </div>
            </div>}
            {loading && <Loader />}
        </>
        );
    };

    return (
        <section id="main-content" tabIndex={-1}>
            <div className="container">
                <div className="row justify-content-center margin-bottom-5">
                    <div className="column-10">
                        {formNotFound && <>
                            <div className="row margin-top-3 justify-content-center">
                                <div className="column-auto">
                                    <div className="text-bold">Form not found</div>
                                    <div>Looks like this form doesn't exist or is no longer available.</div>
                                    <div>Please check the link or contact the person who shared it with you.</div>
                                    <p>If you believe this is in error contact us at <a className='email-link' href="mailto:contact@countbubble.com?subject=Public Form Not Available">contact@countbubble.com</a></p>
                                </div>
                            </div>
                        </>}
                        {!formNotFound && !loading && <>
                            <h1>{form && form.name} Form</h1>
                            {getMainContent()}
                        </>}
                    </div>
                </div>
                <div className="row justify-content-center margin-vertical-5">
                    <div className="column-auto">
                        <span className="text-sub">Powered by <a href="https://www.countbubble.com" target="_blank">countbubble</a></span>
                    </div>
                </div>
            </div>
        </section>)
        ;
}
    const normalizePublicFormField = (field: Form['fields'][number]) => {
        const normalizedField = normalizeDemographicsFormField(field);

        if (!configuredRequiredFieldsOrgSettingsEnabled && normalizedField.name === 'main_profile_date_of_birth') {
            return {
                ...normalizedField,
                required: !optionalDateOfBirthEnabled
            };
        }

        return normalizedField;
    };
